"use client";

import { useState, useEffect } from "react";
import { z } from "zod";
import toast from "react-hot-toast";
import { forgotPassword } from "@/app/lib/api/user";
import Link from "next/link";
import { ForgotPasswordPageSkeleton } from "@/app/components/skeletons/page-skeletons";

const emailSchema = z.object({
  email: z.string().email("Geçerli bir e-posta adresi giriniz"),
});

type ForgotPasswordFormValues = z.infer<typeof emailSchema>;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    // Simulate page loading
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const validateEmail = () => {
    try {
      emailSchema.parse({ email });
      setError("");
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        setError(error.errors[0].message);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail()) return;
    
    setSubmitting(true);
    
    try {
      await forgotPassword({ email });
      
      setSubmitted(true);
      toast.success("Şifre sıfırlama bağlantısı e-posta adresinize gönderildi");
    } catch (error) {
      console.error("Error requesting password reset:", error);
      toast.error(
        error instanceof Error 
          ? error.message 
          : "Şifre sıfırlama isteği gönderilemedi. Lütfen tekrar deneyin."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (pageLoading) {
    return <ForgotPasswordPageSkeleton />;
  }

  return (
    <div className="container mx-auto py-12 px-4">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6">
          <h1 className="text-2xl font-bold text-center mb-6">Şifremi Unuttum</h1>
          
          {submitted ? (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="rounded-full bg-green-100 p-3">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-8 w-8 text-orange-500" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M5 13l4 4L19 7" 
                    />
                  </svg>
                </div>
              </div>
              <p className="text-gray-700">
                Yeni şifreniz <strong>{email}</strong> adresine gönderildi.
                Lütfen e-postanızı kontrol edin ve yeni şifrenizi kullanın.
              </p>
              <div className="mt-6">
                <Link 
                  href="/giris-yap"
                  className="block w-full text-center bg-gradient-to-r from-orange-500 to-orange-500 hover:from-orange-500 hover:to-orange-500 text-white font-medium py-2 px-4 rounded-md transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Giriş Sayfasına Dön
                </Link>
              </div>
            </div>
          ) : (
            <>
              <p className="text-gray-600 mb-6 text-center">
                Şifrenizi sıfırlamak için kayıtlı e-posta adresinizi girin.
                Size şifre sıfırlama bağlantısı göndereceğiz.
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    E-posta Adresi
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`mt-1 block w-full px-3 py-2 border ${
                      error ? "border-red-500" : "border-gray-300"
                    } rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500`}
                    placeholder="ornek@email.com"
                  />
                  {error && (
                    <p className="mt-1 text-sm text-red-600">{error}</p>
                  )}
                </div>
                
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-500 hover:from-orange-500 hover:to-orange-500 text-white font-medium py-2 px-4 rounded-md transition-all duration-300 flex justify-center items-center shadow-md hover:shadow-lg"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                        İşleniyor...
                      </>
                    ) : (
                      "Yeni Şifre Gönder"
                    )}
                  </button>
                </div>
                
                <div className="text-center mt-4">
                  <Link 
                    href="/giris-yap" 
                    className="text-sm text-orange-600 hover:text-orange-800 transition-colors"
                  >
                    Giriş sayfasına dön
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
} 